import type { Pixel, SpatialRectangle } from "@pdfking/renderer-api"
import { Items, SafePromise } from "@pdfking/tfunc"
import type { PDFImage } from "pdf-lib"
import { PDFDocument } from "pdf-lib"

export type BytesToPdfResult = {
  pdf: PDFDocument
  trim_box: SpatialRectangle<Pixel>
  media_box: SpatialRectangle<Pixel>
}

type AttemptFunction = (
  bytes: Uint8Array,
  resolution: number
) => Promise<BytesToPdfResult>

const image_to_pdf = async (
  method: "embedJpg" | "embedPng",
  bytes: Uint8Array,
  resolution: number
): Promise<BytesToPdfResult> => {
  const pdf = await PDFDocument.create()
  const image: PDFImage = await pdf[method](bytes)
  const { width, height } = image.scale(72 / resolution)
  const page = pdf.addPage([width, height])

  page.drawImage(image, {
    x: 0,
    y: 0,
    width,
    height,
  })

  const box: SpatialRectangle<Pixel> = {
    x: 0 as Pixel,
    y: 0 as Pixel,
    width: width as Pixel,
    height: height as Pixel,
  }

  return {
    pdf,
    trim_box: box,
    media_box: box,
  }
}

const attempts: Record<string, AttemptFunction> = {
  "application/pdf": async (bytes: Uint8Array): Promise<BytesToPdfResult> => {
    const pdf = await PDFDocument.load(bytes)
    const page = pdf.getPage(0)
    const { width, height } = page.getSize()
    const box: SpatialRectangle<Pixel> = {
      x: 0 as Pixel,
      y: 0 as Pixel,
      width: width as Pixel,
      height: height as Pixel,
    }
    return {
      pdf,
      trim_box: box,
      media_box: box,
    }
  },
  "image/jpeg": (bytes: Uint8Array, res: number) =>
    image_to_pdf("embedJpg", bytes, res),
  "image/png": (bytes: Uint8Array, res: number) =>
    image_to_pdf("embedPng", bytes, res),
}

const iterate_attempts = function* (
  content_type: string
): Iterable<AttemptFunction> {
  // First try the specific content type if it exists
  if (attempts[content_type]) {
    yield attempts[content_type]
  }

  // Then try all other content types we haven't tried yet
  for (const [type, func] of Items(attempts)) {
    if (type !== content_type) {
      yield func
    }
  }
}

export const BytesToPdf = async (
  bytes: Uint8Array,
  content_type: string,
  resolution = 72
): Promise<BytesToPdfResult> => {
  for (const func of iterate_attempts(content_type)) {
    const { value, error } = await SafePromise(func(bytes, resolution))
    if (error) {
      console.warn("attempting failed", error)
    } else {
      return value
    }
  }
  throw new Error("No suitable conversion method found")
}
